const SESSION_KEY = 'impersonationSession';
const SESSION_PATTERN = /^[a-f0-9]{32}$/;
const READY = 'impersonation-ready';
const TOKEN = 'impersonation-token';
const TIMEOUT = 60000;

export const getImpersonationSession = () => {
    if (typeof window === 'undefined') return null;

    if (window.location.pathname.replace(/\/$/, '') === '/lens') {
        const session = new URLSearchParams(window.location.search).get('session');
        if (!session || !SESSION_PATTERN.test(session)) return null;
        window.sessionStorage.setItem(SESSION_KEY, session);
        return session;
    }

    const session = window.sessionStorage.getItem(SESSION_KEY);
    return session && SESSION_PATTERN.test(session) ? session : null;
};

export const clearImpersonationSession = () => window.sessionStorage.removeItem(SESSION_KEY);

// Open synchronously from the click so popup blockers do not reject the new tab.
// Only a random session identifier is put in the URL; the token stays in memory.
export const openImpersonation = (uid, getToken) => {
    if (getImpersonationSession()) {
        return Promise.reject(new Error('Return to admin before viewing another user.'));
    }

    const session = Array.from(window.crypto.getRandomValues(new Uint8Array(16)))
        .map((value) => value.toString(16).padStart(2, '0'))
        .join('');
    const origin = window.location.origin;
    const userTab = window.open(`/lens/?session=${session}`, '_blank');
    if (!userTab) {
        return Promise.reject(new Error('Allow popups for this site to view a user in a new tab.'));
    }

    return new Promise((resolve, reject) => {
        let requested = false;
        let finished = false;
        const cleanup = () => {
            finished = true;
            window.clearTimeout(timeout);
            window.removeEventListener('message', onMessage);
        };
        const onMessage = async (event) => {
            if (
                event.origin !== origin ||
                event.source !== userTab ||
                event.data?.type !== READY ||
                event.data?.session !== session ||
                requested
            )
                return;

            requested = true;
            try {
                const token = await getToken();
                if (finished) return;
                if (userTab.closed) throw new Error('The user tab was closed. Please try again.');
                userTab.postMessage({ type: TOKEN, session, token, uid }, origin);
                cleanup();
                resolve();
            } catch (error) {
                userTab.postMessage({ type: TOKEN, session, error: true }, origin);
                cleanup();
                reject(
                    new Error('Unable to view this user. Please try again from the users page.')
                );
            }
        };
        const timeout = window.setTimeout(() => {
            cleanup();
            reject(new Error('The user tab did not respond. Please close it and try again.'));
        }, TIMEOUT);
        window.addEventListener('message', onMessage);
    });
};

export const receiveImpersonationToken = () => {
    const session = getImpersonationSession();
    const opener = window.opener;
    if (!session || !opener || opener.closed) {
        return Promise.reject(new Error('Open this view from the admin users page.'));
    }

    return new Promise((resolve, reject) => {
        const cleanup = () => {
            window.clearTimeout(timeout);
            window.removeEventListener('message', onMessage);
        };
        const onMessage = (event) => {
            if (
                event.origin !== window.location.origin ||
                event.source !== opener ||
                event.data?.type !== TOKEN ||
                event.data?.session !== session
            )
                return;

            cleanup();
            if (event.data.error || !event.data.token || !event.data.uid) {
                reject(
                    new Error('Unable to start this user session. Try again from the users page.')
                );
            } else {
                resolve({ token: event.data.token, uid: event.data.uid });
            }
        };
        const timeout = window.setTimeout(() => {
            cleanup();
            reject(new Error('This user session timed out. Open it again from the users page.'));
        }, TIMEOUT);
        window.addEventListener('message', onMessage);
        opener.postMessage({ type: READY, session }, window.location.origin);
    });
};
